import {Ad} from '@models/ad';
import {City} from '@models/city';

export interface FavoriteList {
    id?: string;
    title: string;
    user_id: number;
    favorites: Favorite[];
    share: boolean;
    owner_name: string;
}

export interface FavoriteListPaginated {
    id?: string;
    title: string;
    user_id: number;
    favorites: Favorite[];
    total: number;
    page: number;
    share: boolean;
    owner_name: string;
}

export interface FavoriteMapItem {
    id: string;
    title: string;
    favorite_list_id: string;
    favorite_list_name: string;
    price: number;
    city: string;
    zip_code: string;
    longitude: number;
    latitude: number;
    radius: number;
    status: string;
}

export interface Favorite {
    id: string;
    public_id: string;
    mask: boolean; // Posé par le back : annonce masquée (utilisateur sans accès) -> ne pas recalculer côté front
    ad_id: number;
    ad: Ad;
    ad_city: City;

    // Ad
    created_at: string;
    title: string;
    description: string;
    pictures_string: string;
    pictures_number: string;
    pictures: string[];
    price: number;
    init_price: number;
    estate_sale: string;
    estate_type: string;
    square: number;
    square_land: number;
    rooms: number;
    amount_property_tax: number;
    amount_rent: number;
    amount_copro: number;
    amount_copro_rent: number;
    advice_rent_choice: string;
    advice_price_choice: string;
    advice_price_real: string; // If price data is not found, we use other value
    furnish: number; // 0 => All / 1 Meuble / 2 Nu
    comment: string;
    other_comment: string;
    comment_work: string;
    comment_building: string;
    comment_city: string;
    comment_copro: string;
    adress: string;
    iris: string;
    latitude: number;
    longitude: number;
    exact_adresse: boolean;
    visite_link: string;

    // Travaux
    works_help: number;
    works_square_price: number;
    works_total: number;
    works_not_deduc: number;

    // Bilan des charges
    notary_price: number;
    building_insurance: number;
    agence: number; // %
    amount_rent_insurance: number;
    other_bill: number;
    rental_vacancy: number;
    agence_rental: number; // frais de mise en location ?

    // Prix
    advice_price: number;
    total_price: number; // Dynamicf
    coverage_rate: number; // Dynamic
    advice_price_L: number; // Dynamic, Lower price
    advice_price_U: number; // Dynamic, Upper price
    advice_price_cL: number; // Dynamic, Upper price

    // Loyer
    advice_rent: number; // Dynamic
    advice_rent_amount: number; // Dynamic
    advice_rent_L: number; // Dynamic
    advice_rent_U: number; // Dynamic
    advice_rent_real: string; // Dynamic
    advice_rent_cL: number; // Dynamic
    advice_rent_square: number; // Dynamic

    // Financement
    cash: boolean;
    apport: number;
    duration: number;
    rate: number;
    rate_second: number;
    rate_insurance: number;
    charge: number;

    efficiency_brut: number; // Dynamic
    efficiency_net: number; // Dynamic
    net_rent: number; // Dynamic
    monthly: number; // Dynamic
    loaning: number; // Dynamic
    cash_flow: number; // Dynamic

    lots: Lots[];
    nb_lots: number;
    contacts: Contact[];

    company: boolean;
    works_deduc: boolean;

    // Impots
    agency_fees: number;
    other_fees: number;
    other_fees_work: number;
    furniture_price: number;
    works_duration: number;
    rent_before_work: number;
    annual_rent_increase: number;
    annual_bill_increase: number;
    accouting_fees: number;
    cfe_contribution: number;
    cga_contribution: number;
    is_cga_in_charges: boolean;
    banking_fees: number;
    deposit_fees: number;
    tmi: number;
    annual_income: number;
    annual_income_sci: number;
    fiscal_choice: string;
    fiscal_choice_cat: string;
    location_engagement_duration: number;
    available_work_price: number;
    selling: boolean;
    selling_year: number;
    selling_price: number;
    selling_price_calc: boolean;
    selling_price_evol: number;
    credit_differed_duration: number;
    is_differed_total: boolean;
    shares_number: number;
    buying_year: number;
    fiscal_detailed_view: boolean;

    favorite_list: FavoriteList;
    loading: boolean;

    status: string;
    hide: boolean;

    carac_filled: boolean;

    // Caractéristiques
    lot_copro: number;
    has_copro: boolean;
    floor: number;
    floor_building: number;
    year_building: number;
    orientation: string;

    // Diag
    energy_rate: string;
    energy_rate_value: number;
    ges: string;
    ges_value: number;
    ref_ademe: string;

    // Surfaces annexes
    bedrooms: number;
    bathrooms: number;
    shower_rooms: number;
    restrooms: number;
    parking: number;
    cellar: boolean;
    pool: boolean;
    outside: boolean;

    // Autre
    elevator: boolean;
    urgent: boolean;
    rent: boolean;
    work: boolean;
    renov: boolean;
    disabled_access: boolean;
    guardian: boolean,
    intercom: boolean,
    closed_view: boolean,
    digital_code: boolean,
    owner_type: string;
    heating: string;

    // Displayed calc data
    isSquareValid: boolean;
    smallLink: number;
    providerLib: string;
    fullPriceLib: string;
    quartierLib: string;
}

export interface Lots {
    title: string;
    estate_type: string;
    square: number;
    rooms: number;
    amount_rent: number;
    furnish: number;

    amount_copro_rent: number;

    advice_rent: number;
    advice_rent_amount: number;
    advice_rent_L: number;
    advice_rent_U: number;
    advice_rent_choice: string;
    advice_rent_real?: string; // Dynamic : référence réellement utilisée (repli si la tranche choisie n'a pas de données)
    advice_rent_cL: number;

    works_help: number;
    works_square_price: number;
    works_total: number;

    night_price: number;
    night_number: number;

    nb_lots: number;
    window: string;
    heating: string;
    dpe: string;
    dpe_after: string;
    comment: string;
}

// Statut par défaut d'un lead ; la liste complète des statuts est renvoyée par GET app/leads (statuses)
export const LEAD_DEFAULT_STATUS = 'A contacter';

// Lead reçu sur une annonce publiée (GET app/leads)
export interface PublisherLead {
    id: number;
    created_at: string;
    status: string; // valeur prise dans statuses
    comment: string; // texte libre du publieur
    favorite_id: string; // favori depuis lequel l'annonce a été publiée
    favorite_list_id: string; // liste de ce favori ('all' en repli si absente)
    ad: {
        id: number;
        title: string;
        price: number;
        city_name: string;
        zip_code: string;
        real_estate_type: string;
        is_active: boolean;
        offmarket: boolean;
        published_at: string;
    };
    user: {
        id: number;
        firstname: string;
        lastname: string;
        email: string;
        mobile: string;
    };
}

export interface PublisherLeadsResponse {
    leads: PublisherLead[];
    statuses: string[];
}

export interface Contact {
    name: string;
    phone: string;
    email: string;
}
